import React, {useEffect, useMemo, useRef, useState} from "react"
import FormSectionAccordion from "../../../../../components/FormSectionAccordion"
import theme from "../../../../../theme/theme"
import {ReactComponent as CheckIcon} from "../../../../../assets/icons/check-icon.svg"
import PropTypes from "prop-types"
import {renderGrid} from "../../../../../components/FieldRenderer"
import {mapPayloadRatingEmissao, mapRatingEmissao, ratingFields} from "./constants"
import {Box} from "@mui/material"
import ButtonWithIcon from "../../../../../components/Button/Button"
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline"
import EmissaoService from "../../../../../../services/emissao-service"
import DuplaConfirmacao from "../../../../../components/DuplaConfirmacao/DuplaConfirmacao"
import {dispatchSnackbar} from "../../../../../components/SnackBar/snackbar-bus"
import {getApiErrorMessage} from "src/v2/utils/api-error"

const RatingSection = (
    {
        caracteristicasState,
        garantiasState,
        state,
        setRatingState,
        setSectionField,
        setLoading,
        isValid,
        showError,
        actions,
        defaultOpen = false,
        syncToken = 0,
        autoCompleteController = {}
    }) => {

    const ratingService = useMemo(() => new EmissaoService(), [])

    const [cetipRating, setCetipRating] = useState(null)
    const ultimaConsultaRef = useRef(null)
    const [cadastrarRatingOpen, setCadastrarRatingOpen] = useState(false)
    const [ratingRegistered, setRatingRegistered] = useState(false)

    useEffect(() => {
        const cetip = caracteristicasState?.cetip
        const devedor = caracteristicasState?.devedor

        if ((cetip ?? "").length < 4) {
            ultimaConsultaRef.current = null
            return
        }

        const chaveConsulta = `${cetip}|${devedor ?? ""}`

        if (ultimaConsultaRef.current === chaveConsulta) return

        ultimaConsultaRef.current = chaveConsulta
        setCetipRating(cetip)
        loadRatingEmissao(cetip, devedor)
    }, [caracteristicasState?.cetip, caracteristicasState?.devedor])

    const loadRatingDevedor = (cpfCnpj) => {
        if ((cpfCnpj ?? "").length < 11) return

        setLoading(true)
        ratingService.getUltimoRatingDevedor(cpfCnpj)
            .then((response) => {
                const {body: {data}} = response
                if (data !== null) {
                    const ratingEmissao = mapRatingEmissao(data)

                    setRatingState({
                        ...ratingEmissao,
                        ID: null,
                    })
                    setRatingRegistered(false)
                    return
                }

                dispatchSnackbar("Rating devedor não cadastrado.", "warning")
            })
            .catch((err) => {
                console.error("Erro ao consultar rating devedor", err)
            })
            .finally(() => setLoading(false))
    }

    const loadRatingEmissao = (cetip, cpfCnpj) => {
        if ((cetip ?? "").length < 4) return

        setLoading(true)
        ratingService.getUltimoRatingEmissaoCetip(cetip)
            .then((response) => {
                const {body: {data}} = response
                if (data !== null) {
                    setRatingState(mapRatingEmissao(data))
                    setRatingRegistered(true)
                    return
                }

                setRatingRegistered(false)
                setSectionField("rating", {})
                loadRatingDevedor(cpfCnpj)
            })
            .catch((err) => {
                console.error("Não foi possível carregar os dados de rating emissão", err)
            })
            .finally(() => setLoading(false))
    }

    const createRatingEmissao = () => {
        const body = mapPayloadRatingEmissao(
            cetipRating,
            state,
            caracteristicasState,
            garantiasState
        )

        setLoading(true)
        ratingService.ratingEmissao("post", body)
            .then((response) => {
                const mapped = mapRatingEmissao(response.body)

                setRatingState(mapped)
                setRatingRegistered(true)
                dispatchSnackbar("Rating cadastrado com sucesso!", "success")
            })
            .catch((error) => {
                dispatchSnackbar(
                    getApiErrorMessage(error, "Não foi possível cadastrar o rating."),
                    "error"
                )
                console.error(error)
            })
            .finally(() => {
                setLoading(false)
                setCadastrarRatingOpen(false)
            })
    }

    const updateRatingEmissao = () => {
        const {ID} = state

        if (!ID) {
            dispatchSnackbar("Rating de emissão sem ID para atualização.", "error")
            return
        }

        const payload = mapPayloadRatingEmissao(
            cetipRating,
            state,
            caracteristicasState,
            garantiasState
        )

        setLoading(true)
        ratingService.ratingEmissao("patch", ID, payload)
            .then((response) => {
                const mapped = mapRatingEmissao(response.body)

                setRatingState(mapped)
                setRatingRegistered(true)
                dispatchSnackbar("Rating atualizado com sucesso!", "success")
            })
            .catch((error) => {
                dispatchSnackbar(
                    getApiErrorMessage(error, "Não foi possível atualizar o rating."),
                    "error"
                )
                console.error(error)
            })
            .finally(() => {
                setCadastrarRatingOpen(false)
                setLoading(false)
            })
    }

    return (
        <>
            <FormSectionAccordion
                title={"Rating Emissão"}
                icon={<CheckIcon style={{ color: theme.palette.icon.main, width: 20, height: 20 }} />}
                isValid={isValid}
                showError={showError}
                actions={actions}
                defaultOpen={defaultOpen}
                syncToken={syncToken}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "right",
                        mt: 3,
                        mb: 3,
                    }}
                >
                    <ButtonWithIcon size="md" icon={<AddCircleOutlineIcon />}
                        onClick={() => setCadastrarRatingOpen(true)}>
                        {ratingRegistered ? "ATUALIZAR RATING" : "CADASTRAR RATING"}
                    </ButtonWithIcon>
                </Box>
                {renderGrid(
                    ratingFields,
                    state,
                    (key) => (value) => setSectionField("rating", key, value),
                    undefined,
                    undefined,
                    (key) => (value) => setSectionField("rating", key, value),
                    autoCompleteController
                )}
            </FormSectionAccordion>

            <DuplaConfirmacao
                open={cadastrarRatingOpen}
                cancel={() => setCadastrarRatingOpen(false)}
                confirm={ratingRegistered ? updateRatingEmissao : createRatingEmissao}
                title={`${ratingRegistered ? "Atualizar" : "Cadastrar"} Rating`}
                description={`O rating da emissão ${cetipRating} será ${ratingRegistered ? "atualizado" : "cadastrado"}. Deseja continuar?`}
            />

        </>
    )
}

RatingSection.propTypes = {
    caracteristicasState: PropTypes.object,
    garantiasState: PropTypes.object,
    state: PropTypes.object.isRequired,
    setRatingState: PropTypes.func.isRequired,
    setSectionField: PropTypes.func.isRequired,
    setLoading: PropTypes.func.isRequired,
    isValid: PropTypes.bool,
    showError: PropTypes.bool,
    actions: PropTypes.node,
    defaultOpen: PropTypes.bool,
    syncToken: PropTypes.number,
    autoCompleteController: PropTypes.object,
}

export default RatingSection